import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, MapPin, Clock, CurrencyDollar, Wrench, Trophy, X } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { Lightbox } from './Lightbox';

const projects = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/12ordvan_bcdc.jpeg",
    title_fr: "Aménagement du complexe BCC Kananga",
    title_en: "BCC Kananga Complex Development",
    category_fr: "Aménagement & Construction",
    category_en: "Development & Construction",
    location: "Kananga, Kasaï-Central",
    client: "BCC Kananga",
    budget_fr: "350 000 USD",
    budget_en: "$350,000",
    duration_fr: "8 mois",
    duration_en: "8 months",
    tech_fr: "Béton armé, pavés autobloquants, drainage intégré",
    tech_en: "Reinforced concrete, interlocking pavers, integrated drainage",
    challenge_fr: "Coordination de 5 corps de métier simultanés sur un site occupé avec maintien des activités bancaires",
    challenge_en: "Coordination of 5 simultaneous trades on an occupied site while maintaining banking operations",
    result_fr: "Livraison avec 2 semaines d'avance, zéro interruption des services bancaires",
    result_en: "Delivered 2 weeks early, zero disruption to banking services",
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/pnecsvke_jkm6.jpeg",
    title_fr: "Dalots de drainage — Réseau urbain Kananga",
    title_en: "Drainage Culverts — Kananga Urban Network",
    category_fr: "Ouvrages d'Assainissement",
    category_en: "Drainage Structures",
    location: "Kananga, Kasaï-Central",
    client: "Gouvernement Provincial",
    budget_fr: "220 000 USD",
    budget_en: "$220,000",
    duration_fr: "6 mois",
    duration_en: "6 months",
    tech_fr: "Dalots en béton armé NF EN 1992, géotextile, enrochement",
    tech_en: "NF EN 1992 reinforced concrete culverts, geotextile, riprap",
    challenge_fr: "Sol argileux instable nécessitant des fondations profondes et un drainage temporaire pendant les travaux",
    challenge_en: "Unstable clay soil requiring deep foundations and temporary drainage during construction",
    result_fr: "Réduction de 70% des inondations urbaines dans le quartier ciblé",
    result_en: "70% reduction in urban flooding in the target neighborhood",
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/vv3f5qn8_jkm7.jpeg",
    title_fr: "Murs de soutènement en gabions — Stabilisation de talus",
    title_en: "Gabion Retaining Walls — Slope Stabilization",
    category_fr: "Génie Civil",
    category_en: "Civil Engineering",
    location: "Province du Kasaï",
    client: "PAF",
    budget_fr: "180 000 USD",
    budget_en: "$180,000",
    duration_fr: "4 mois",
    duration_en: "4 months",
    tech_fr: "Gabions galvanisés, géotextile anti-érosion, calcul Eurocode 7",
    tech_en: "Galvanized gabions, anti-erosion geotextile, Eurocode 7 design",
    challenge_fr: "Pente instable de 45° avec risque de glissement en saison des pluies",
    challenge_en: "Unstable 45° slope with landslide risk during rainy season",
    result_fr: "Stabilisation permanente, aucun mouvement de terrain observé sur 3 ans",
    result_en: "Permanent stabilization, no ground movement observed over 3 years",
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/s4e1mph2_jkm4.jpeg",
    title_fr: "Canal de drainage principal — Réseau hydraulique",
    title_en: "Main Drainage Channel — Hydraulic Network",
    category_fr: "Ouvrages Hydrauliques",
    category_en: "Hydraulic Structures",
    location: "Mbuji-Mayi, Kasaï-Oriental",
    client: "Mairie de Mbuji-Mayi",
    budget_fr: "280 000 USD",
    budget_en: "$280,000",
    duration_fr: "7 mois",
    duration_en: "7 months",
    tech_fr: "Canaux trapézoïdaux en béton, dimensionnement Manning-Strickler, déversoirs",
    tech_en: "Trapezoidal concrete channels, Manning-Strickler sizing, weirs",
    challenge_fr: "Traversée de zones habitées densément peuplées avec déviations de trafic complexes",
    challenge_en: "Crossing densely populated areas with complex traffic diversions",
    result_fr: "3.2 km de canaux livrés, capacité d'évacuation de 15 m³/s",
    result_en: "3.2 km of channels delivered, 15 m³/s evacuation capacity",
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/smtv48sg_jkm1.jpeg",
    title_fr: "Programme de forage hydraulique — Eau potable rurale",
    title_en: "Hydraulic Drilling Program — Rural Drinking Water",
    category_fr: "Forage & Hydraulique",
    category_en: "Drilling & Hydraulics",
    location: "Province du Kasaï-Central",
    client: "UNICEF / Gouvernement",
    budget_fr: "450 000 USD",
    budget_en: "$450,000",
    duration_fr: "12 mois",
    duration_en: "12 months",
    tech_fr: "Forage rotatif MFT 200m, pompage solaire, analyse ISO 22475",
    tech_en: "DTH rotary drilling 200m, solar pumping, ISO 22475 analysis",
    challenge_fr: "Accès difficile en zone rurale, logistique d'acheminement d'équipements lourds sur 250 km de pistes",
    challenge_en: "Difficult access in rural areas, heavy equipment logistics over 250 km of dirt roads",
    result_fr: "10 puits opérationnels, 25 000 bénéficiaires directs en eau potable",
    result_en: "10 operational wells, 25,000 direct beneficiaries of clean water",
  },
  {
    id: 6,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/awc8j13q_jkm9.jpeg",
    title_fr: "Laboratoire géotechnique — Centre d'analyses",
    title_en: "Geotechnical Laboratory — Analysis Center",
    category_fr: "Études & Analyses",
    category_en: "Studies & Analysis",
    location: "Kananga, Kasaï-Central",
    client: "JKM Engineering (investissement propre)",
    budget_fr: "120 000 USD",
    budget_en: "$120,000",
    duration_fr: "3 mois",
    duration_en: "3 months",
    tech_fr: "Essais Proctor, granulométrie laser, CBR, limites d'Atterberg, ASTM D1557",
    tech_en: "Proctor tests, laser granulometry, CBR, Atterberg limits, ASTM D1557",
    challenge_fr: "Calibration aux standards internationaux dans un environnement sans accès fiable à l'électricité",
    challenge_en: "Calibration to international standards in an environment without reliable electricity access",
    result_fr: "Capacité d'analyse de 200+ échantillons/mois, certifié ASTM",
    result_en: "Analysis capacity of 200+ samples/month, ASTM certified",
  },
  {
    id: 7,
    image: "https://customer-assets.emergentagent.com/job_7e1961ac-5810-49fb-bfe1-8dd4c900e77c/artifacts/9ilk38cb_pave6.jpeg",
    title_fr: "Pavage autobloquant — Allées institutionnelles",
    title_en: "Interlocking Paving — Institutional Walkways",
    category_fr: "Travaux de Pavage",
    category_en: "Paving Works",
    location: "Kananga, Kasaï-Central",
    client: "BCC Kananga",
    budget_fr: "95 000 USD",
    budget_en: "$95,000",
    duration_fr: "3 semaines",
    duration_en: "3 weeks",
    tech_fr: "Pavés autobloquants 80mm, lit de sable stabilisé, bordures béton, caniveaux intégrés",
    tech_en: "80mm interlocking pavers, stabilized sand bed, concrete curbs, integrated gutters",
    challenge_fr: "Travaux en saison des pluies avec gestion des eaux de ruissellement en continu",
    challenge_en: "Construction during rainy season with continuous stormwater management",
    result_fr: "550 m² livrés, drainage intégré fonctionnel, garantie 10 ans",
    result_en: "550 m² delivered, functional integrated drainage, 10-year warranty",
  },
  {
    id: 8,
    image: "https://customer-assets.emergentagent.com/job_jkm-engineering-rdc/artifacts/lhxni973_jkm8.jpeg",
    title_fr: "Fondations profondes & Terrassement — Zone industrielle",
    title_en: "Deep Foundations & Earthworks — Industrial Zone",
    category_fr: "Travaux de Terrassement",
    category_en: "Earthworks",
    location: "Kananga, Kasaï-Central",
    client: "Secteur Privé",
    budget_fr: "310 000 USD",
    budget_en: "$310,000",
    duration_fr: "5 mois",
    duration_en: "5 months",
    tech_fr: "Pieux battus, semelles filantes, remblai compacté Proctor 95%, essais de charge",
    tech_en: "Driven piles, strip footings, 95% Proctor compacted fill, load testing",
    challenge_fr: "Nappe phréatique à 2m de profondeur nécessitant un rabattement permanent",
    challenge_en: "Water table at 2m depth requiring permanent dewatering",
    result_fr: "Capacité portante de 300 kPa atteinte, conformité Eurocode 7",
    result_en: "300 kPa bearing capacity achieved, Eurocode 7 compliant",
  },
];

const ProjectModal = ({ project, language, onClose, t }) => {
  if (!project) return null;
  const title = language === 'fr' ? project.title_fr : project.title_en;
  const details = [
    { icon: CurrencyDollar, label: t.projects.budget, value: language === 'fr' ? project.budget_fr : project.budget_en },
    { icon: Clock, label: t.projects.duration, value: language === 'fr' ? project.duration_fr : project.duration_en },
    { icon: Wrench, label: t.projects.tech, value: language === 'fr' ? project.tech_fr : project.tech_en },
    { icon: Trophy, label: t.projects.result, value: language === 'fr' ? project.result_fr : project.result_en },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div initial={{ y: 30, scale: 0.95 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.95 }} className="relative z-10 bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-[#FF6B00] flex items-center justify-center transition-colors">
          <X size={20} className="text-white" weight="bold" />
        </button>
        <img src={project.image} alt={title} className="w-full aspect-video object-cover" />
        <div className="p-8">
          <p className="text-[#FF6B00] text-xs font-medium uppercase tracking-wider mb-2">{language === 'fr' ? project.category_fr : project.category_en}</p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0B1320] tracking-tight mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h2>
          <p className="text-sm text-[#475569] flex items-center gap-1 mb-6"><MapPin size={14} className="text-[#FF6B00]" />{project.location}{project.client && ` — ${project.client}`}</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {details.map((d, i) => (
              <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <d.icon size={16} className="text-[#FF6B00]" weight="bold" />
                  <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{d.label}</p>
                </div>
                <p className="text-sm text-[#0B1320] font-medium">{d.value}</p>
              </div>
            ))}
          </div>
          <div className="bg-[#0B1320] p-4">
            <p className="text-xs text-[#FF6B00] uppercase tracking-wider mb-2">{t.projects.challenge}</p>
            <p className="text-sm text-white/80">{language === 'fr' ? project.challenge_fr : project.challenge_en}</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const ProjectsSection = () => {
  const { t, language } = useLanguage();
  const [selectedProject, setSelectedProject] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  return (
    <section id="projects" className="py-24 lg:py-32 bg-white" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">Portfolio</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1320] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="projects-title">{t.projects.title}</h2>
          </div>
          <p className="text-base text-[#475569] max-w-md mt-4 md:mt-0" style={{ fontFamily: "'Manrope', sans-serif" }}>{t.projects.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {projects.slice(0, 6).map((project, index) => (
            <motion.div key={project.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }} className="group relative overflow-hidden cursor-pointer" onClick={() => setSelectedProject(project)}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={project.image} alt={language === 'fr' ? project.title_fr : project.title_en} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320] via-[#0B1320]/20 to-transparent opacity-80" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-[#FF6B00] text-xs font-medium uppercase tracking-[0.15em] mb-2">{language === 'fr' ? project.category_fr : project.category_en}</p>
                <h3 className="text-lg font-semibold text-white mb-1 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{language === 'fr' ? project.title_fr : project.title_en}</h3>
                <div className="flex items-center gap-3 text-xs text-white/50">
                  <span className="flex items-center gap-1"><MapPin size={12} />{project.location}</span>
                  <span>{language === 'fr' ? project.budget_fr : project.budget_en}</span>
                </div>
              </div>
              <div className="absolute top-4 right-4 w-10 h-10 bg-white/0 group-hover:bg-[#FF6B00] flex items-center justify-center transition-all duration-300 opacity-0 group-hover:opacity-100">
                <ArrowUpRight size={20} className="text-white" weight="bold" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {projects.slice(6).map((project, index) => (
            <motion.div key={project.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }} className="group relative overflow-hidden cursor-pointer" onClick={() => setSelectedProject(project)}>
              <div className="aspect-square overflow-hidden">
                <img src={project.image} alt={language === 'fr' ? project.title_fr : project.title_en} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320] to-transparent opacity-80" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-[#FF6B00] text-[10px] font-medium uppercase tracking-wider mb-1">{language === 'fr' ? project.category_fr : project.category_en}</p>
                <h3 className="text-sm font-semibold text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{language === 'fr' ? project.title_fr : project.title_en}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && <ProjectModal project={selectedProject} language={language} onClose={() => setSelectedProject(null)} t={t} />}
      </AnimatePresence>

      <Lightbox images={projects} currentIndex={lightboxIndex} isOpen={lightboxOpen} onClose={() => setLightboxOpen(false)} onPrev={() => setLightboxIndex((p) => (p === 0 ? projects.length - 1 : p - 1))} onNext={() => setLightboxIndex((p) => (p === projects.length - 1 ? 0 : p + 1))} language={language} />
    </section>
  );
};
